import { useReactiveVar } from "@apollo/client";
import { memo, useEffect, useMemo, useState } from "react";
import * as Styled from "./style";
import { VideoSeasonTabContents } from "./video-season-tab-contents";
import { VideoSeasonTabTitle } from "./video-season-tab-title";
import { useRouter } from "next/router";
import { MULTI_SELECT_TABS } from "~/components/blocks/product/const";
import { isVideoSeason } from "~/components/blocks/product/type-guard";
import { isFirstFocusPurchasedTabVar } from "~/components/stores/detail-product/store";
import { PATHS } from "~/const/paths";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { G_VideoSeasonType } from "~/lib/graphql";

type VideoSeasonTabProps = {
  videoData: G_GetVideoQuery["video"];
  isAdultBlock: boolean;
};

/**
 * 作品末端 | 複数話 | タブとそのタブに紐づくコンテンツを表示するコンポーネント
 * @param props {@link VideoSeasonTabProps}
 */
export const VideoSeasonTab = memo((props: VideoSeasonTabProps) => {
  const { videoData, isAdultBlock } = props;
  const router = useRouter();

  const totalMainEpisodeCount = useMemo(
    () =>
      (videoData.seasonType === G_VideoSeasonType.MultiEpisodes &&
        isVideoSeason(videoData) &&
        videoData.episodes.total) ||
      0,
    [videoData.id]
  );

  const totalPurchasedEpisodeCount = useMemo(
    () =>
      (videoData.seasonType === G_VideoSeasonType.MultiEpisodes &&
        isVideoSeason(videoData) &&
        videoData.purchasedContents?.total) ||
      0,
    [videoData.id]
  );

  const totalSpecialEpisodeCount = useMemo(
    () => (isVideoSeason(videoData) ? videoData.specialEpisode.total : 0),
    [videoData.id]
  );

  const totalPvEpisodeCount = useMemo(() => (isVideoSeason(videoData) ? videoData.pvEpisode.total : 0), [videoData.id]);

  const isCurrentLibraryPage = `${router.pathname}/` === PATHS.MY_LIBRARY;
  const isPurchase = useReactiveVar(isFirstFocusPurchasedTabVar) || isCurrentLibraryPage;

  const defaultTabValue = useMemo(() => {
    if (!isAdultBlock) return MULTI_SELECT_TABS.PRODUCT_INFO;
    if (isPurchase && totalPurchasedEpisodeCount > 0) return MULTI_SELECT_TABS.PURCHASED;
    if (totalMainEpisodeCount > 0) return MULTI_SELECT_TABS.EPISODE;
    if (totalPurchasedEpisodeCount > 0) return MULTI_SELECT_TABS.PURCHASED;
    if (totalSpecialEpisodeCount > 0) return MULTI_SELECT_TABS.SPECIAL;
    return MULTI_SELECT_TABS.PRODUCT_INFO;
  }, [videoData.id]);

  const [selectedTabValue, setSelectedTabValue] = useState(defaultTabValue);

  // memo:useStateの値は、setterが走るまで再レンダリングされても変更されないためこちらの処理が必要
  useEffect(() => {
    if (!isPurchase) return;
    setSelectedTabValue(defaultTabValue);
    isFirstFocusPurchasedTabVar(false);
  }, [defaultTabValue]);

  return (
    <Styled.ArticleRootTab>
      <VideoSeasonTabTitle
        selectedTabValue={selectedTabValue}
        onChangeTabValue={setSelectedTabValue}
        seasonType={videoData.seasonType}
        totalEpisodeLength={totalMainEpisodeCount}
        totalPurchasedLength={totalPurchasedEpisodeCount}
        totalSpecialLength={totalSpecialEpisodeCount}
        totalPreviewLength={totalPvEpisodeCount}
        isAdultBlock={isAdultBlock}
      />

      <VideoSeasonTabContents
        videoData={videoData}
        selectedTabValue={selectedTabValue}
        totalEpisodeLength={totalMainEpisodeCount}
        totalPurchasedLength={totalPurchasedEpisodeCount}
        totalSpecialLength={totalSpecialEpisodeCount}
        totalPreviewLength={totalPvEpisodeCount}
      />
    </Styled.ArticleRootTab>
  );
});

VideoSeasonTab.displayName = "VideoSeasonTab";
